import React, { useState, useEffect } from 'react';
import { 
  TextInput, 
  Select, 
  Stack,
  Button,
  Group,
  Paper,
  Text,
  Box,
  Switch,
  Textarea,
  NumberInput,
  Collapse,
  Modal,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import moment from 'moment-timezone';
import { useSalon } from '../../../../../context/SalonContext';
import { useCalendar } from '../../../../../context/CalendarContext';
function TimeBlockForm({ initialData, onSubmit, onDelete, isEdit, isLoading }) {
  const { businessInfo, employees } = useSalon();
  const { selectedDate } = useCalendar();
  const [formData, setFormData] = useState({
    startDate: selectedDate || new Date(),
    endDate: selectedDate || new Date(),
    startTime: initialData?.startTime || '',
    endTime: initialData?.endTime || '',
    artistId: initialData?.resourceId || '',
    type: initialData?.type || '',
    description: initialData?.description || '',
    isRecurring: !!initialData?.recurrenceInterval,
    recurrenceInterval: initialData?.recurrenceInterval || 1,
    recurrenceUnit: initialData?.recurrenceUnit || 'Day',
    recurrenceEndDate: initialData?.recurrenceEndDate ? moment(initialData.recurrenceEndDate).toDate() : null,
    appliesTo: 'single' // 'single', 'hereafter', or 'all'
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Initialize form with edit data if provided
  useEffect(() => {
    if (initialData && isEdit) {
      setFormData(prev => ({
        ...prev,
        startDate: moment(initialData.start).toDate(),
        endDate: moment(initialData.end).toDate(),
        startTime: moment(initialData.start).format('HH:mm'),
        endTime: moment(initialData.end).format('HH:mm'),
        artistId: initialData.resourceId,
        type: initialData.type,
        description: initialData.description,
        isRecurring: !!initialData.recurrenceInterval,
        recurrenceInterval: initialData.recurrenceInterval || 1,
        recurrenceUnit: initialData.recurrenceUnit || 'Day',
        recurrenceEndDate: initialData.recurrenceEndDate ? moment(initialData.recurrenceEndDate).toDate() : null
      }));
    }
  }, [initialData, isEdit]);

  // Generate time slots from 7:00 AM to 9:00 PM in 15-minute intervals
  const timeSlots = React.useMemo(() => {
    const slots = [];
    const startTime = moment().set({ hour: 0, minute: 0 });
    const endTime = moment().set({ hour: 23, minute: 45 });

    while (startTime <= endTime) {
      slots.push({
        value: startTime.format('HH:mm'),
        label: startTime.format('h:mm A')
      });
      startTime.add(15, 'minutes');
    }
    return slots;
  }, []);

  const blockTypes = [
    { value: 'Break', label: 'Break' },
    { value: 'Vacation', label: 'Vacation' },
    { value: 'Sick', label: 'Sick' },
    { value: 'Personal', label: 'Personal' },
    { value: 'Holiday', label: 'Holiday' },
    { value: 'Other', label: 'Other' }
  ];

  const recurrenceUnits = [
    { value: 'Day', label: 'Days' },
    { value: 'Week', label: 'Weeks' },
    { value: 'Month', label: 'Months' },
    { value: 'Year', label: 'Years' }
  ];

  const appliesToOptions = [
    { value: 'single', label: 'This time block only' },
    { value: 'hereafter', label: 'This and future time blocks' },
    { value: 'all', label: 'All time blocks in series' }
  ];

  // Handle start date changes
  const handleStartDateChange = (value) => {
    setFormData(prev => ({
      ...prev,
      startDate: value,
      // If end date was same as previous start date, update it
      endDate: prev.endDate && prev.startDate && 
               prev.endDate.getTime() === prev.startDate.getTime() ? 
               value : prev.endDate || value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create start datetime in UTC
    const startDateTime = moment.tz(formData.startDate, businessInfo?.timezone)
      .hour(moment(formData.startTime, 'HH:mm').hour())
      .minute(moment(formData.startTime, 'HH:mm').minute())
      .format();

    // Create end datetime in UTC
    const endDateTime = moment.tz(formData.endDate, businessInfo?.timezone)
      .hour(moment(formData.endTime, 'HH:mm').hour())
      .minute(moment(formData.endTime, 'HH:mm').minute())
      .format();

    const timeBlockData = {
      type: formData.type,
      start_dt: startDateTime,
      end_dt: endDateTime,
      description: formData.description,
    };

    // Add recurrence fields if enabled
    if (isEdit) {
        timeBlockData.id = initialData.id;
        timeBlockData.applies_to = formData.appliesTo;
    }
    else {
        timeBlockData.staff_id = formData.artistId;
        if (formData.isRecurring) {
            timeBlockData.recurrence_interval = formData.recurrenceInterval;
            timeBlockData.recurrence_unit = formData.recurrenceUnit;
            timeBlockData.recurrence_end_date = moment(formData.recurrenceEndDate).format('YYYY-MM-DD')
        }
    }

    onSubmit(timeBlockData);
  };

  const handleDeleteClick = () => {
    // If it's the root instance of a series
    if (initialData?.recurrenceParentId === initialData?.id) {
      setIsDeleteModalOpen(true);
    }
    // If it's part of a series but not the root
    else if (initialData?.recurrenceParentId) {
      setIsDeleteModalOpen(true);
    }
    // If it's not part of a series
    else {
      onDelete('single');
    }
  };

  return (
    <Paper p="sm">
      {employees && (
        <form onSubmit={handleSubmit}>
          <Stack spacing="sm">
            <Group grow>
              <DateInput
                label="Start Date"
                required
                value={formData.startDate}
                onChange={handleStartDateChange}
                clearable
                valueFormat="MMM D, YYYY"
                placeholder="Select date"
                firstDayOfWeek={0}
              />
              <Select
                label="Start Time"
                required
                placeholder="Start Time"
                data={timeSlots}
                value={formData.startTime}
                onChange={(value) => setFormData({ ...formData, startTime: value })}
                searchable
                maxDropdownHeight={200}
              />
            </Group>

            <Group grow>
              <DateInput
                label="End Date"
                required
                value={formData.endDate}
                onChange={(value) => setFormData({ ...formData, endDate: value })}
                minDate={formData.startDate}
                clearable
                valueFormat="MMM D, YYYY"
                placeholder="Select date"
                firstDayOfWeek={0}
              />
              <Select
                label="End Time"
                required
                placeholder="End Time"
                data={timeSlots}
                value={formData.endTime}
                onChange={(value) => setFormData({ ...formData, endTime: value })}
                searchable
                maxDropdownHeight={200}
              />
            </Group>

            <Select
              label="Artist"
              required
              placeholder="Select Artist"
              data={employees.map(emp => ({
                value: emp.id,
                label: `${emp.first_name} ${emp.last_name}`
              }))}
              value={formData.artistId}
              onChange={(value) => setFormData({ ...formData, artistId: value })}
              clearable
              disabled={isEdit}
            />

            <Select
              label="Type"
              required
              placeholder="Select block type"
              data={blockTypes}
              value={formData.type}
              onChange={(value) => setFormData({ ...formData, type: value })}
              clearable
            />

            <Textarea
              label="Description"
              placeholder="Enter additional details"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            {isEdit && initialData?.recurrenceParentId && (
              <Select
                label="Apply to"
                required
                data={appliesToOptions}
                value={formData.appliesTo}
                onChange={(value) => setFormData({ ...formData, appliesTo: value })}
              />
            )}

            <Group position="apart" mt="xs">
              <Text size="sm">Is Recurring</Text>
              <Switch
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.currentTarget.checked })}
                disabled={isEdit} // Disable switching recurring mode in edit
              />
            </Group>

            <Collapse in={formData.isRecurring}>
              <Stack spacing="sm">
                <Group grow>
                  <NumberInput
                    label="Repeat every"
                    required={formData.isRecurring}
                    min={1}
                    value={formData.recurrenceInterval}
                    onChange={(value) => setFormData({ ...formData, recurrenceInterval: value })}
                    disabled={isEdit} // Disable editing recurrence details in edit mode
                  />
                  <Select
                    label="Unit"
                    required={formData.isRecurring}
                    data={recurrenceUnits}
                    value={formData.recurrenceUnit}
                    onChange={(value) => setFormData({ ...formData, recurrenceUnit: value })}
                    disabled={isEdit} // Disable editing recurrence details in edit mode
                  />
                </Group>
                <DateInput
                  label="End Date"
                  value={formData.recurrenceEndDate}
                  required={formData.isRecurring}
                  onChange={(value) => setFormData({ ...formData, recurrenceEndDate: value })}
                  minDate={formData.startDate}
                  clearable={!isEdit}
                  valueFormat="MMM D, YYYY"
                  placeholder="Select end date"
                  firstDayOfWeek={0}
                  disabled={isEdit} // Disable editing recurrence end date in edit mode
                />
              </Stack>
            </Collapse>

            <Group grow mt="xl">
              <Button 
                type="submit"
                fullWidth 
                size="md"
                color="violet"
                loading={isLoading}
              >
                {isEdit ? 'Update Time Block' : 'Save Time Block'}
              </Button>
            </Group>

            {isEdit && (
              <Button
                color="red"
                variant="subtle"
                fullWidth
                size="md"
                onClick={handleDeleteClick}
                loading={isLoading}
              >
                Delete Time Block
              </Button>
            )}
          </Stack>
        </form> 
      )}

      {/* Modal for recurring block deletion options */}
      <Modal
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={
          initialData?.recurrenceParentId === initialData?.id
            ? "Delete Recurring Time Block"
            : "Delete Time Block Options"
        }
        size="sm"
        centered
      >
        {initialData?.recurrenceParentId === initialData?.id ? (
          <>
            <Text size="sm" mb="lg">
              Deleting the root instance of a series will delete the entire series. 
              Please click Confirm Delete to delete the series or Cancel to keep it.
            </Text>
            <Group position="apart" mt="xl">
              <Button 
                variant="default" 
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                color="red"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  onDelete('all');
                }}
                loading={isLoading}
              >
                Confirm Delete
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Stack spacing="md">
              <Text size="sm" mb="md">
                Which time blocks would you like to delete?
              </Text>
              <Button 
                variant="light" 
                color="red"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  onDelete('single');
                }}
                loading={isLoading}
              >
                This time block only
              </Button>
              <Button 
                variant="light"
                color="red"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  onDelete('hereafter');
                }}
                loading={isLoading}
              >
                This and future time blocks
              </Button>
              <Button 
                variant="light"
                color="red"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  onDelete('all');
                }}
                loading={isLoading}
              >
                All time blocks in series
              </Button>
              <Button 
                variant="subtle"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
            </Stack>
          </>
        )}
      </Modal>
    </Paper>
  );
}

export default TimeBlockForm; 